import {
  Box,
  Button,
  CircularProgress,
  createStyles,
  IconButton,
  makeStyles,
  Table,
  TableBody,
  Theme,
  Tooltip,
  Typography,
  withStyles,
} from '@material-ui/core'
import {
  ClearOutlined,
  AddOutlined,
  ErrorOutlineOutlined,
} from '@material-ui/icons'
import { AjvError } from '@rjsf/core'
import { LoadingContainer } from 'components/Common/LoadingContainer'
import { WorkingStepIconSvg } from 'components/Common/WorkingSteps/WorkingStepIcon'
import { LocalizedTypography } from 'components/Localization/LocalizedTypography'
import { LocalizedWorkingStepLabel } from 'components/Localization/LocalizedWorkingStepLabel'
import { WorkingStepsController } from 'controllers/WorkingStepsController'
import { projectSelectors } from 'features/BillOfMaterials/store/selectors/projectSelectors'
import { WorkingStepDto } from 'services/APIs/InternalAPI/internal-api.contracts'
import { useAppSelector } from 'store/configureStore'

type Props = {
  titleTKey: string
  workingSteps: WorkingStepDto[]
  setCurrentWorkingStep: (workingStep: WorkingStepDto) => void
  currentWorkingStep: WorkingStepDto
  handleDelete?: (workingStep: WorkingStepDto) => void | Promise<void>
  handleAddWorkingStep?: (workingStep: WorkingStepDto) => void
  loading: Record<string, boolean>
  validationErrors: Record<string, AjvError[]>
}

const TableButton = withStyles(() =>
  createStyles({
    root: {
      justifyContent: 'flex-start',
      color: 'inherit',
    },
  })
)(Button)

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    isActive: {
      backgroundColor: theme.palette.action.selected,
    },
    deleteButton: {
      color: theme.palette.error.main,
    },
    warningButton: {
      color: theme.palette.warning.main,
    },
  })
)

export const WorkingStepTable = (props: Props) => {
  const cn = useStyles()

  const isBuyer = useAppSelector(projectSelectors.ProjectInBuyerViewSelector)

  const isRemovable = (workingStep: WorkingStepDto) => {
    if (isBuyer && workingStep.notRemovable) {
      return false
    }

    return true
  }

  return (
    <Box marginBottom={1}>
      <LocalizedTypography
        translationKey={props.titleTKey}
        variant="subtitle1"
      />
      <LoadingContainer
        loading={
          props.loading[WorkingStepsController.WorkingStepsOperations.LOAD_DATA]
        }
        noMessage
      >
        <Table>
          <TableBody>
            {props.workingSteps.length === 0 ? (
              <tr>
                <LocalizedTypography
                  component="td"
                  style={{ textAlign: 'center' }}
                  translationKey="common:no-items-available"
                  defaultValue="not defined"
                  variant="caption"
                  color="textSecondary"
                />
              </tr>
            ) : (
              props.workingSteps
                .sort((a, b) => {
                  if (a.primaryWorkingStep < b.primaryWorkingStep) {
                    return -1
                  }
                  if (a.primaryWorkingStep > b.primaryWorkingStep) {
                    return 1
                  }
                  return 0
                })
                .map((workingStep) => (
                  <tr
                    key={workingStep.key}
                    onClick={() => props.setCurrentWorkingStep(workingStep)}
                    className={`available-working-step ${
                      props.currentWorkingStep?.key === workingStep.key
                        ? cn.isActive
                        : ''
                    }
                  `}
                  >
                    <td>
                      {props.validationErrors &&
                      Object.keys(props.validationErrors[workingStep.key] || {})
                        .length ? (
                        <Tooltip
                          title={
                            <pre>
                              {JSON.stringify(props.validationErrors, null, 2)}
                            </pre>
                          }
                        >
                          <ErrorOutlineOutlined
                            className={`validation-error ${cn.warningButton}`}
                          />
                        </Tooltip>
                      ) : null}
                      <TableButton
                        fullWidth
                        onClick={() => props.setCurrentWorkingStep(workingStep)}
                      >
                        <Box marginRight={2}>
                          <WorkingStepIconSvg
                            workingStepType={workingStep.primaryWorkingStep}
                            attributes={{
                              width: '3em',
                            }}
                          />
                        </Box>
                        <span>
                          <LocalizedWorkingStepLabel
                            workingStepType={workingStep.primaryWorkingStep}
                          />
                        </span>
                      </TableButton>
                    </td>
                    <td className="is-right-aligned">
                      {props.loading[
                        `${WorkingStepsController.WorkingStepsOperations.DELETE_DATA}_${workingStep.key}`
                      ] ? (
                        <Typography color="textSecondary" component="span">
                          <CircularProgress color="inherit" />
                        </Typography>
                      ) : props.handleDelete && isRemovable(workingStep) ? (
                        <IconButton
                          // className="delete-button"
                          // size="small"
                          onClick={(e) => {
                            e.stopPropagation()
                            props.handleDelete(workingStep)
                          }}
                          className={cn.deleteButton}
                          color="inherit"
                        >
                          <ClearOutlined />
                        </IconButton>
                      ) : (
                        props.handleAddWorkingStep && (
                          <IconButton
                            className="delete-button"
                            onClick={() => {
                              props.handleAddWorkingStep(workingStep)
                            }}
                          >
                            <AddOutlined />
                          </IconButton>
                        )
                      )}
                    </td>
                  </tr>
                ))
            )}
          </TableBody>
        </Table>
      </LoadingContainer>
    </Box>
  )
}
